import React, { useState, useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Circle, useMap } from 'react-leaflet';
import { MapPin, Info } from 'lucide-react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icon in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom marker icon with brand color
const createCustomIcon = () => {
  return L.divIcon({
    className: 'custom-map-marker',
    html: `
      <div style="
        width: 36px;
        height: 36px;
        background: #004B8D;
        border: 3px solid white;
        border-radius: 50% 50% 50% 0;
        transform: rotate(-45deg);
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
      ">
        <div style="
          width: 8px;
          height: 8px;
          background: white;
          border-radius: 50%;
          transform: rotate(45deg);
        "></div>
      </div>
    `,
    iconSize: [36, 36],
    iconAnchor: [18, 36],
  });
};

// Component to disable zoom and interaction
const MapController = () => {
  const map = useMap();
  
  useEffect(() => {
    map.scrollWheelZoom.disable();
    map.doubleClickZoom.disable();
    map.touchZoom.disable();
    map.boxZoom.disable();
    map.keyboard.disable();
    map.dragging.disable();
  }, [map]);
  
  return null;
};

// Geocode location using Nominatim (OpenStreetMap)
const geocodeLocation = async (location) => {
  try {
    // Add "Australia" to improve geocoding accuracy
    const searchQuery = location.includes('Australia') 
      ? location 
      : `${location}, Australia`;
    
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}&limit=1`,
      {
        headers: {
          'User-Agent': 'HireHub-VehicleRental/1.0'
        }
      }
    );
    
    const data = await response.json();
    
    if (data && data.length > 0) {
      return {
        lat: parseFloat(data[0].lat),
        lng: parseFloat(data[0].lon),
        displayName: data[0].display_name
      };
    }
    
    return null;
  } catch (error) {
    console.error('Geocoding error:', error);
    return null;
  }
};

// Add random offset to coordinates for privacy (within ~500m)
const addPrivacyOffset = (lat, lng) => {
  // Random offset between -0.003 and 0.003 degrees (roughly 300m)
  const latOffset = (Math.random() - 0.5) * 0.006;
  const lngOffset = (Math.random() - 0.5) * 0.006;
  
  return {
    lat: lat + latOffset,
    lng: lng + lngOffset
  };
};

/**
 * PickupLocationMap Component
 * Shows approximate pickup location for a vehicle listing
 * Privacy-focused: shows suburb area, not exact address
 */
const PickupLocationMap = ({ location }) => {
  const [coordinates, setCoordinates] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  
  // Normalize location to string
  const locationString = typeof location === 'object' 
    ? `${location.city || ''}${location.city && location.state ? ', ' : ''}${location.state || ''}`
    : (location || '');
  
  // Memoize custom icon to prevent recreation
  const customIcon = useMemo(() => createCustomIcon(), []);
  
  useEffect(() => {
    if (!locationString) {
      setLoading(false);
      setError(true);
      return;
    }
    
    const fetchCoordinates = async () => {
      setLoading(true);
      setError(false);
      
      const result = await geocodeLocation(locationString);
      
      if (result) {
        // Add privacy offset to coordinates
        const offsetCoords = addPrivacyOffset(result.lat, result.lng);
        setCoordinates({
          ...offsetCoords,
          displayName: result.displayName
        });
      } else {
        setError(true);
      }
      
      setLoading(false);
    };
    
    fetchCoordinates();
  }, [locationString]);
  
  // Loading state
  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-slate-100 p-5" data-testid="pickup-location-map">
        <div className="flex items-center gap-2 mb-4">
          <MapPin className="w-5 h-5 text-[#004B8D]" />
          <h3 className="font-semibold text-slate-900">Pickup Area</h3>
        </div>
        <div className="aspect-[16/9] bg-slate-100 rounded-xl flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-slate-200 border-t-[#004B8D]"></div>
        </div>
      </div>
    );
  }
  
  // Error state or no location
  if (error || !coordinates) {
    return (
      <div className="bg-white rounded-2xl border border-slate-100 p-5" data-testid="pickup-location-map">
        <div className="flex items-center gap-2 mb-4">
          <MapPin className="w-5 h-5 text-[#004B8D]" />
          <h3 className="font-semibold text-slate-900">Pickup Area</h3>
        </div>
        <div className="aspect-[16/9] bg-slate-50 rounded-xl flex flex-col items-center justify-center text-slate-500">
          <MapPin className="w-10 h-10 mb-2 text-slate-300" />
          <p className="text-sm font-medium">{locationString || 'Location not specified'}</p>
          <p className="text-xs text-slate-400 mt-1">Map unavailable for this location</p>
        </div>
        <div className="flex items-start gap-2 mt-3 p-3 bg-slate-50 rounded-lg">
          <Info className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-slate-500">
            Exact pickup address will be provided after booking confirmation.
          </p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-5 relative z-0" data-testid="pickup-location-map">
      <div className="flex items-center gap-2 mb-4">
        <MapPin className="w-5 h-5 text-[#004B8D]" />
        <h3 className="font-semibold text-slate-900">Pickup Area</h3>
      </div>
      
      {/* Map Container - isolated z-index context */}
      <div className="aspect-[16/9] rounded-xl overflow-hidden border border-slate-200 relative isolate" style={{ zIndex: 0 }}>
        <MapContainer
          center={[coordinates.lat, coordinates.lng]}
          zoom={14}
          style={{ height: '100%', width: '100%', position: 'relative', zIndex: 0 }}
          zoomControl={false}
          attributionControl={false}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <MapController />
          
          {/* Approximate area circle */}
          <Circle
            center={[coordinates.lat, coordinates.lng]}
            radius={500}
            pathOptions={{
              color: '#004B8D',
              fillColor: '#004B8D',
              fillOpacity: 0.1,
              weight: 2,
              opacity: 0.5
            }}
          />
          
          {/* Custom marker */}
          <Marker 
            position={[coordinates.lat, coordinates.lng]} 
            icon={customIcon}
          />
        </MapContainer>
      </div>
      
      {/* Location label */}
      <div className="flex items-center gap-2 mt-3">
        <span className="text-sm font-medium text-slate-700">{locationString}</span>
      </div>
      
      {/* Privacy message */}
      <div className="flex items-start gap-2 mt-3 p-3 bg-slate-50 rounded-lg">
        <Info className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-slate-500">
          Approximate pickup area shown for privacy. Exact pickup address will be provided after booking confirmation.
        </p>
      </div>
    </div>
  );
};

export default PickupLocationMap;
